// src/components/doctorsJournal/JournalContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { deriveKey, generateSalt, hashPassword } from './encryptionService';
import { JournalAPI } from './journalApi';

const JournalContext = createContext();

const SESSION_TIMEOUT = 90 * 60 * 1000; // 90 minutes

export const JournalProvider = ({ children }) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(true)
  const timeoutRef = useRef(null);

  // Check if user has journal password setup
  useEffect(() => {
    const checkPassword = async () => {
      try {
        const status = await JournalAPI.getPasswordStatus();
        setHasPassword(status.data.hasPassword);
        setLoading(false)
      } catch (error) {
        console.error('Failed to check password:', error);
        setLoading(false)
      }
    };
    checkPassword();
  }, []);


  // Auto-lock after timeout
  useEffect(() => {
    if (isUnlocked) {
      resetTimeout();
    }
    return () => clearTimeout(timeoutRef.current);
  }, [isUnlocked]);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      lock();
    }, SESSION_TIMEOUT);
  };

  const setupPassword = async (password) => {
    const key = password; // Store password directly as key

    try {
      const hash = await hashPassword(password);
      await JournalAPI.setupPassword(hash); // Empty salt

      setEncryptionKey(key);
      setHasPassword(true);
      setIsUnlocked(true);
    } catch (error) {
      console.error('Failed to setup password:', error);
      throw error;
    }
  };

  const unlock = async (password) => {
    try {
      const hash = await hashPassword(password);
      const result = await JournalAPI.verifyPassword(hash);
      if (!result.data.isValid) {
        return false;
      }

      setEncryptionKey(password);
      setIsUnlocked(true);
      return true;
    } catch (error) {
      console.error('Failed to verify password:', error);
      return false;
    }
  };

  const lock = () => {
    setEncryptionKey(null);
    setIsUnlocked(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return (
    <JournalContext.Provider
      value={{
        isUnlocked,
        encryptionKey,
        hasPassword,
        setupPassword,
        loading,
        unlock,
        lock,
        resetTimeout,
      }}
    >
      {children}
    </JournalContext.Provider>
  );
};

export const useJournal = () => {
  const context = useContext(JournalContext);
  if (!context) {
    throw new Error('useJournal must be used within JournalProvider');
  }
  return context;
};